import { useReferralLeads, type ReferralLead } from "@/hooks/useReferralLeads";
import { usePaginatedReferralLeads } from "@/hooks/usePaginatedReferralLeads";
import { useOutreachTasks } from "@/hooks/useOutreachTasks";
import { useActivityLog } from "@/hooks/useActivityLog";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Trash2, Loader2, Mail, ChevronDown, ChevronUp, Play, MessageSquare, CheckCircle2, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import EmailComposerModal from "@/components/EmailComposerModal";
import SequencePreviewDialog from "@/components/SequencePreviewDialog";
import ReferralImportModal from "@/components/ReferralImportModal";
import ReferralExportModal from "@/components/ReferralExportModal";
import { format } from "date-fns";
import { getSequenceForSourceType } from "@/lib/outreach-sequences";

const STATUSES = ["New", "Contacted", "Meeting Scheduled", "Partner", "Not Interested"];
const PER_PAGE = 20;

function emailStatusBadge(status: string) {
  switch (status) {
    case "Draft": return <Badge variant="secondary">Draft</Badge>;
    case "Sent": return <Badge className="bg-primary/20 text-primary">Sent</Badge>;
    case "Scheduled": return <Badge variant="outline">Scheduled</Badge>;
    default: return <span className="text-xs text-muted-foreground">—</span>;
  }
}

function activityIcon(type: string) {
  switch (type) {
    case "note": return <MessageSquare className="h-3 w-3 text-muted-foreground" />;
    case "email": return <Mail className="h-3 w-3 text-primary" />;
    case "task_completed": return <CheckCircle2 className="h-3 w-3 text-green-600" />;
    default: return <MessageSquare className="h-3 w-3 text-muted-foreground" />;
  }
}

export default function ReferralOutreach() {
  const { user } = useAuth();
  const { profile } = useProfile();
  const seg = (profile as any)?.segment || "homecare";
  const { referralLeads, loading, updateReferralLead, deleteReferralLead, refetch } = useReferralLeads();
  const { tasks, createTasks, completeTask, uncompleteTask } = useOutreachTasks();
  const { activities, addActivity } = useActivityLog();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [composerLead, setComposerLead] = useState<ReferralLead | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [previewLead, setPreviewLead] = useState<ReferralLead | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get("status") || "all";
  const [page, setPage] = useState(0);
  const {
    leads: pagedLeads,
    totalCount: pagedTotalCount,
    loading: pagedLoading,
    refetch: refetchPaged,
  } = usePaginatedReferralLeads({ page, perPage: PER_PAGE, status: statusFilter });
  const totalPages = Math.max(1, Math.ceil(pagedTotalCount / PER_PAGE));

  function handleStatusFilterChange(value: string) {
    setPage(0);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value === "all") next.delete("status");
      else next.set("status", value);
      return next;
    });
  }

  async function handleStatusChange(id: string, status: string) {
    try {
      await updateReferralLead(id, { status });
      await addActivity({ referral_lead_id: id, activity_type: "status_change", content: `Status changed to ${status}` });
      await refetchPaged();
      toast.success("Status updated");
    } catch { toast.error("Failed to update status"); }
  }

  async function handleDelete(id: string) {
    setDeletingId(id);
    try {
      await deleteReferralLead(id);
      await refetchPaged();
      toast.success("Referral removed");
    }
    catch { toast.error("Failed to delete"); }
    finally { setDeletingId(null); setConfirmDeleteId(null); }
  }

  async function handleLaunchSequence(lead: ReferralLead) {
    if (!user) return;
    const sourceType = lead.source_type || "Hospital Discharge Planner";
    const steps = getSequenceForSourceType(sourceType);
    const today = new Date();
    const taskList = steps.map(s => ({
      user_id: user.id,
      referral_lead_id: lead.id,
      title: s.title,
      description: s.description,
      due_date: new Date(today.getTime() + s.dayOffset * 86400000).toISOString().split("T")[0],
      sequence_step: s.step,
      sequence_type: sourceType,
    }));
    try {
      await createTasks(taskList);
      await addActivity({ referral_lead_id: lead.id, activity_type: "note", content: `Started ${sourceType} outreach sequence (4 touches)` });
      toast.success("Outreach sequence started!");
    } catch { toast.error("Failed to start sequence"); }
    setPreviewLead(null);
  }

  async function handleCompleteTask(taskId: string, leadId: string, title: string) {
    try {
      await completeTask(taskId);
      await addActivity({ referral_lead_id: leadId, activity_type: "task_completed", content: `Completed: ${title}` });
      toast.success("Task completed");
    } catch { toast.error("Failed to complete task"); }
  }

  async function handleAddNote(leadId: string) {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      await addActivity({ referral_lead_id: leadId, activity_type: "note", content: noteText.trim() });
      const lead = referralLeads.find(r => r.id === leadId);
      const existingNotes = (lead as any)?.notes || "";
      const updated = existingNotes ? `${existingNotes}\n${noteText.trim()}` : noteText.trim();
      await updateReferralLead(leadId, { notes: updated } as any);
      await refetchPaged();
      setNoteText("");
      toast.success("Note added");
    } catch { toast.error("Failed to add note"); }
    finally { setSavingNote(false); }
  }

  const leadTasks = (leadId: string) => tasks.filter(t => t.referral_lead_id === leadId);
  const leadActivities = (leadId: string) => activities.filter(a => a.referral_lead_id === leadId);
  const hasSequence = (leadId: string) => tasks.some(t => t.referral_lead_id === leadId);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3">
        <div>
          <h2 className="text-2xl font-bold text-foreground">{seg === "grouphome" ? "Coordinator Outreach" : "Referral Outreach"}</h2>
          <p className="text-sm text-muted-foreground">{seg === "grouphome" ? "Manage your saved support coordinators and track outreach." : "Manage your saved referral partners and track outreach progress."}</p>
        </div>
        <div className="flex flex-row flex-wrap gap-2 items-center">
          <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
            <SelectTrigger className="w-44 h-9 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={() => setShowImport(true)}><Upload className="h-4 w-4 mr-1" /> Import</Button>
          <Button size="sm" variant="outline" onClick={() => setShowExport(true)}><Download className="h-4 w-4 mr-1" /> Export</Button>
        </div>
      </div>

      {loading || pagedLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : referralLeads.length === 0 ? (
        <div className="rounded-xl border bg-card p-12 text-center">
          <p className="text-muted-foreground">No referral partners saved yet. Use "Find Referrals" to discover partners.</p>
        </div>
      ) : pagedTotalCount === 0 ? (
        <div className="rounded-xl border bg-card p-12 text-center">
          <p className="text-muted-foreground">No referral partners with status "{statusFilter}".</p>
        </div>
      ) : (
        <div className="space-y-3">
          {pagedLeads.map(rl => (
            <div key={rl.id} className="rounded-xl border bg-card overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3">
                <Button size="icon" variant="ghost" className="shrink-0" onClick={() => setExpandedId(expandedId === rl.id ? null : rl.id)}>
                  {expandedId === rl.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{rl.org_name}</div>
                  <div className="text-xs text-muted-foreground">{rl.contact_name}{rl.title ? ` · ${rl.title}` : ""}</div>
                </div>
                <div className="text-xs text-muted-foreground">{rl.source_type || "—"}</div>
                <Select value={rl.status} onValueChange={v => handleStatusChange(rl.id, v)}>
                  <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <div className="flex items-center gap-1">{emailStatusBadge(rl.email_status)}</div>
                <div className="text-xs text-muted-foreground w-20 text-right">
                  {rl.last_contacted_at ? format(new Date(rl.last_contacted_at), "MMM d") : "—"}
                </div>
                <div className="flex items-center gap-1">
                  {!hasSequence(rl.id) && (
                    <Button size="sm" variant="outline" className="gap-1 text-xs h-7" onClick={() => setPreviewLead(rl)}>
                      <Play className="h-3 w-3" /> Sequence
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="gap-1 text-xs h-7" onClick={() => setComposerLead(rl)}>
                    <Mail className="h-3 w-3" />
                    {rl.email_draft_subject ? "View" : "Email"}
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setConfirmDeleteId(rl.id)} disabled={deletingId === rl.id}>
                    {deletingId === rl.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3 text-muted-foreground" />}
                  </Button>
                </div>
              </div>
              {expandedId === rl.id && (
                <div className="border-t px-4 py-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold text-foreground">Outreach Tasks</h4>
                    {leadTasks(rl.id).length === 0 ? (
                      <p className="text-xs text-muted-foreground">No sequence started. Click "Sequence" to begin a 4-touch outreach.</p>
                    ) : (
                      <div className="space-y-2">
                        {leadTasks(rl.id).map(task => (
                          <div key={task.id} className={`flex items-start gap-2 rounded-lg border p-2 text-xs ${task.completed ? "bg-muted/50" : ""}`}>
                            <Checkbox checked={task.completed} onCheckedChange={checked => { if (checked) handleCompleteTask(task.id, rl.id, task.title); else uncompleteTask(task.id); }} className="mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <div className={`font-medium ${task.completed ? "line-through text-muted-foreground" : ""}`}>Step {task.sequence_step}: {task.title}</div>
                              <div className="text-muted-foreground">{task.description}</div>
                              <div className="text-muted-foreground mt-1">Due: {format(new Date(task.due_date), "MMM d, yyyy")}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold text-foreground">Notes & Activity</h4>
                    <div className="flex gap-2">
                      <Textarea placeholder="Add a note..." value={noteText} onChange={e => setNoteText(e.target.value)} className="text-xs min-h-[60px]" />
                      <Button size="sm" onClick={() => handleAddNote(rl.id)} disabled={savingNote || !noteText.trim()} className="shrink-0">
                        {savingNote ? <Loader2 className="h-3 w-3 animate-spin" /> : "Add"}
                      </Button>
                    </div>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {leadActivities(rl.id).length === 0 ? (
                        <p className="text-xs text-muted-foreground">No activity yet.</p>
                      ) : (
                        leadActivities(rl.id).map(a => (
                          <div key={a.id} className="flex items-start gap-2 text-xs">
                            {activityIcon(a.activity_type)}
                            <div className="flex-1 min-w-0">
                              <span className="text-foreground">{a.content}</span>
                              <span className="text-muted-foreground ml-2">{format(new Date(a.created_at), "MMM d, h:mm a")}</span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
          {totalPages > 1 && (
            <div className="flex items-center justify-between gap-2 pt-2">
              <span className="text-xs text-muted-foreground">{pagedTotalCount} total</span>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</Button>
                <span className="text-sm font-semibold">{page + 1} of {totalPages}</span>
                <Button size="sm" variant="outline" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {previewLead && (
        <SequencePreviewDialog
          open={!!previewLead}
          onOpenChange={o => { if (!o) setPreviewLead(null); }}
          steps={getSequenceForSourceType(previewLead.source_type || "Hospital Discharge Planner")}
          sourceType={previewLead.source_type || "Hospital Discharge Planner"}
          onConfirm={() => handleLaunchSequence(previewLead)}
        />
      )}

      {composerLead && (
        <EmailComposerModal
          open={!!composerLead}
          onOpenChange={open => { if (!open) setComposerLead(null); }}
          lead={composerLead}
          onUpdate={async (id, updates) => {
            await updateReferralLead(id, updates);
            await refetchPaged();
            const updated = referralLeads.find(r => r.id === id);
            if (updated) setComposerLead({ ...updated, ...updates } as ReferralLead);
          }}
        />
      )}

      <ReferralImportModal open={showImport} onOpenChange={setShowImport} onImported={() => { refetch(); refetchPaged(); }} />
      <ReferralExportModal open={showExport} onOpenChange={setShowExport} />

      <AlertDialog open={!!confirmDeleteId} onOpenChange={open => { if (!open) setConfirmDeleteId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this referral?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the referral partner and its outreach history. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => confirmDeleteId && handleDelete(confirmDeleteId)}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
